import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { toast } from './Toast';
import Title from './Title';
import Block, { BlockHeader } from './Block';
import List from './List';
import ListItem from './ListItem';
import Icon from './Icon';
import { getString as s, getStringElement } from '../lib/strings';
import { getShow as show } from '../lib/show';
import copyRef from '../lib/copyRef';
import { ConfirmModal, NoticeModal } from './Modal';
import Layout, { LayoutBody, LayoutHeader } from './Layout';
import Banner from './Banner';
import Loader from './Loader';
import BackButton from './BackButton/index';
import { Brands } from './../common/constants';

const LinkBlock = styled.div`
  ${(props) => props.theme.type.link};
  font-family: ${(props) => props.theme.type.link.font};
  cursor: pointer;
  padding: ${(props) => (props.brand !== Brands.Wireless) && props.theme.whitespace.small};
  text-align: ${(props) => props.theme.type.linkBlock.textAlign };
  margin-bottom: 20px;
  font-size: ${(props) => props.theme.type.linkBlock.fontSize};
  font-style: ${(props) => props.theme.type.linkBlock.fontStyle};
  font-weight: ${(props) => props.theme.type.linkBlock.fontWeight};
  line-height: ${(props) => props.theme.type.linkBlock.lineHeight};
`;

const OptoutAllCopy = styled.div`
  text-align: center;
  padding: 0;
  margin-top: -32px;
  font-size: 12px !important;
`;

const OptoutCopyLink = styled.a`
  font-size: 12px !important;
  font-family: TimesDigital !important;
`;

class Products extends Component {
  static propTypes = {
    products: PropTypes.arrayOf(PropTypes.object).isRequired,
    externals: PropTypes.arrayOf(PropTypes.object),
    globalUserPermissions: PropTypes.arrayOf(PropTypes.object).isRequired,
    partners: PropTypes.arrayOf(PropTypes.object),
    fetchProducts: PropTypes.func.isRequired,
    fetchPartners: PropTypes.func.isRequired,
    marketing: PropTypes.shape({
      id: PropTypes.string,
      displayName: PropTypes.string,
      userPermissions: PropTypes.arrayOf(PropTypes.object)
    }),
    history: PropTypes.shape({
      length: PropTypes.number,
      push: PropTypes.func.isRequired
    }).isRequired,
    setGlobal: PropTypes.func.isRequired,
    acsSession: PropTypes.bool.isRequired,
    globalStatus: PropTypes.bool,
    error: PropTypes.bool,
    brand: PropTypes.string.isRequired,
    arePermissionsFetching: PropTypes.bool.isRequired,
    arePartnersFetching: PropTypes.bool.isRequired
  };
  static defaultProps = {
    partners: [],
    marketing: {},
    globalStatus: false,
    error: false
  };
  /*
  Note to future devs:
  This is a quick and dirty way to get these modals going (time pressure).
  This should all be swapped out for an app level notification system (toats, modals etc...)
  driven centrally via redux.
  There is a nice pattern suggested by Abramov on SO.
  - Alex.
   */
  constructor() {
    super();
    this.state = {
      modal: false
    };
  }

  componentDidMount() {
    this.props.fetchProducts();
    this.props.fetchPartners();
  }

  onConfirmOptOut = () => {
    this.props.setGlobal(this.props.globalUserPermissions, false)
    window.scrollTo({
      behavior: "smooth",
      top: 0
    });
  }

  toggleGlobal = (e, currentValue) => {
    if (e.type === 'keypress' && e.key !== 'Enter' && e.key !== ' ') {
      return;
    }
    e.preventDefault();
    const { setGlobal, globalUserPermissions } = this.props;
    if (currentValue) {
      this.setState({ modal: 'confirm' });
    } else {
      this.setState({ modal: 'inform' });
      setGlobal(globalUserPermissions, true);
    }
  };

  closeModals = () => this.setState({ modal: false });

  render() {

    const {
      arePartnersFetching,
      arePermissionsFetching,
      products,
      externals,
      partners,
      history,
      marketing,
      globalStatus,
      acsSession,
      brand
    } = this.props;

    if (this.props.error) {
      const title = s('channel.error.title', 'Sorry!');
      const body = s(
        'channel.error.subtitle',
        'There was a problem. Please refresh your browser.'
      );
      toast.error(title, body);
    }

    const showBanner = (!!products.length || marketing.id) && !globalStatus;
    const showMyAccount = show('myAccount' ,true);
    const showNewsletter = show('newsletter',true);
    const showPartners = show('partners',true);
    const showFaq = show('faq',true);
    const showPermissionText = show('products.permissionText',true);

    return (
      <Layout>
        {showBanner && (
          <Banner
            brand={brand}
            primary={s(
              'product.optOut.notice.title',
              'You are unsubscribed from all notifications'
            )}
            secondary={s(
              'product.optOut.notice.text',
              'Turn these on to get the best experience'
            )}
            actionLabel={s('product.optOut.notice.button', 'Turn On')}
            actionHandler={(e) => this.toggleGlobal(e, globalStatus)}
          />
        )}

        {acsSession && showMyAccount && (
          <LayoutHeader>
            <BackButton href={s('product.myAccount.link', '')}>
              {s('product.myAccount.copy','')}
            </BackButton>
          </LayoutHeader>
        )}
        <LayoutBody brand={brand}>
          <Title
            brand={brand}
            primary={s('product.title', 'All Products')}
            secondary={getStringElement(
              'product.subtitle',
              'The permission center lets you control how and and what we can contact you about'
            )}
          />
          <Block>
            <Fragment>
              {showPermissionText && (
                <BlockHeader
                  primary={s('product.permission.title', 'Product Permissions')}
                  secondary={s(
                    'product.permission.subtitle',
                    'Manage updates and offers from products you have signed up to.'
                  )}
                />
              )}

              {arePermissionsFetching && (
                <Loader isLoading={arePermissionsFetching} />
              )}

              {!arePermissionsFetching &&
                products.length !== 0 && (
                  <List id="permissions_list">
                    {products.map((product) => (
                      <ListItem
                        key={`prod-${product.id}`}
                        primary={copyRef(product, 'product_name')}
                        addon={<Icon brand={brand} name="rightChevron" />}
                        onClick={() => history.push(`/products/${product.id}`)}
                        button
                      />
                    ))}
                  </List>
                )}
            </Fragment>
            {marketing.userPermissions && marketing.userPermissions.length && (
              <Fragment>
                <BlockHeader
                  primary={s(
                    'product.marketing.title',
                    'Marketing Permissions'
                  )}
                  secondary={s(
                    'product.marketing.subtitle',
                    'Manage other offers and promotions.'
                  )}
                />
                <List id="marketing_list">
                  <ListItem
                    key={`prod-${marketing.id}`}
                    primary={copyRef(marketing, 5)}
                    addon={<Icon brand={brand} name="rightChevron" />}
                    onClick={() => history.push(`/products/${marketing.id}`)}
                    button
                  />
                </List>
              </Fragment>
            )}
            {
              this.props.brand !== 'tls' && this.props.brand !== 'tls_com' &&
              <LinkBlock brand={this.props.brand}>
                <span
                  role="button"
                  tabIndex={0}
                  id="global_opt_toggle"
                  aria-label={globalStatus ? s('product.optOut.link', 'Opt-out of all') : s('product.optIn.link', 'Opt-in of all')}
                  onKeyPress={(e) => this.toggleGlobal(e, globalStatus)}
                  onClick={(e) => this.toggleGlobal(e, globalStatus)}
                >
                  {globalStatus ? s('product.optOut.link', 'Opt-out of all') : s('product.optIn.link', 'Opt-in of all')}
                </span>
              </LinkBlock>
            }
            {this.props.brand === 'tnl' && <OptoutAllCopy><i>This will not unsubscribe you from newsletter preferences, to do that click <OptoutCopyLink rel="noopener noreferrer" href="https://home.thetimes.co.uk/myNews" target="_blank">here</OptoutCopyLink></i></OptoutAllCopy>}
            {this.props.brand === 'tnl_com' && <OptoutAllCopy><i>This will not unsubscribe you from newsletter preferences, to do that click <OptoutCopyLink rel="noopener noreferrer" href="https://home.thetimes.com/myNews" target="_blank">here</OptoutCopyLink></i></OptoutAllCopy>}
          </Block>

          {(showNewsletter || showPartners) && this.props.brand !== 'tls' && this.props.brand !== 'tls_com' && (

            <Block>
              {arePartnersFetching && <Loader isLoading={arePartnersFetching} />}

              {!arePartnersFetching && partners.length > 0 && (
                <Fragment>
                  <BlockHeader
                    primary={s('product.external.title', 'External Partners')}
                    secondary={s(
                      'product.external.subtitle',
                      'You will need to manage the below permissions on our partner sites.'
                    )}
                  />
                  <BlockHeader
                    primary={s('product.partner.title', 'Partner Sites')}
                    secondary={s(
                      'product.partner.subtitle',
                      'Manage communication sent out by our partners'
                    )}
                  />
                  <List id="partner_list">
                    {partners.map((partner) => partner.newWindow ?
                      <ListItem
                        key={`part-${partner.id}`}
                        className={`part-${partner.displayName.toLowerCase().replace(' ', '')}`}
                        primary={partner.displayName}
                        onClick={() => window.open(partner.uri)}
                        addon={<Icon brand={brand} name="link" />}
                      />
                      :
                      <ListItem
                        key={`part-${partner.id}`}
                        className={`part-${partner.displayName.toLowerCase().replace(' ', '')}`}
                        primary={partner.displayName}
                        onClick={() => history.push(partner.uri)}
                        addon={<Icon brand={brand} name="rightChevron" />}
                      />)}

                    {externals.map((external) => (<ListItem
                      key={`prod-${external.id}`}
                      primary={copyRef(external, 'product_name')}
                      addon={<Icon brand={brand} name="rightChevron" />}
                      onClick={() => history.push(`/products/${external.id}`)}
                      button
                    />))}
                  </List>
                </Fragment>
              )}

              {showNewsletter && (
                <Fragment>
                  <BlockHeader
                    primary={s('product.newsletter.title', 'Newsletters')}
                    secondary={s(
                    'product.newsletter.subtitle',
                    'Stay up to date with our newsletters.'
                    )}
                  />
                  <List id="newsletter_list">
                    <ListItem
                      className="news-general"
                      key="news-general"
                      primary={s('product.newsletter.link', 'Manage Newsletters')}
                      onClick={() => window.open(s('product.newsletter.uri', '#'))}
                      addon={<Icon brand={brand} name="link" />}
                      button
                    />
                  </List>
                </Fragment>
              )}
            </Block>
          )}
          {showFaq && (
            <Block>
              <BlockHeader
                primary={s('product.faq.title', 'FAQs')}
                secondary={s(
                'product.faq.subtitle',
                'If you’re having problems or have any questions about managing your permissions you may find the answers in our FAQs sections'
                )}
              />
              <List id="faq_list">
                <ListItem
                  key="faq-general"
                  primary={s('product.faq.link', "Permission Center FAQ's")}
                  addon={<Icon brand={brand} name="link" />}
                  onClick={() => window.open(s('product.faq.uri', '#'))}
                  button
                />
              </List>
            </Block>
          )}

          <ConfirmModal
            brand={this.props.brand}
            isOpen={this.state.modal === 'confirm'}
            onClose={this.closeModals}
            onConfirm={this.onConfirmOptOut}
            title={s(
              'product.optOut.confirm.title',
              'Disable all notifications'
            )}
            text={s(
              'product.optOut.confirm.text',
              'This will disable all communication across all products'
            )}
            notice={s(
              'product.optOut.confirm.notice',
              'Changes may take a few days to take effect'
            )}
            confirmLabel={s('product.optOut.confirm.yes', 'Yes')}
            cancelLabel={s('product.optOut.confirm.no', 'No')}
          />
          {!this.props.error && <NoticeModal
            brand={this.props.brand}
            isOpen={this.state.modal === 'inform'}
            onClose={this.closeModals}
            text={s(
              'product.optIn.notice',
              'All permissions have been enabled'
            )}
          />}
        </LayoutBody>

      </Layout>
    );
  }
}

export default Products;
